import { BCF_APP_ID } from '@bcf-vanilla-ts-v1-configurators/tokens/app/token';
import type { BcfAppId } from '@bcf-vanilla-ts-v1-configurators/tokens/app/types';
import type { WorkerToolkit } from '@bcf-vanilla-ts-v1-platforms/platform-worker/worker/worker-toolkit';
import { provideWorkerToolkit } from '@bcf-vanilla-ts-v1-platforms/platform-worker/worker/worker-toolkit';
import { provideDiTokenValue } from '@bcf-vanilla-ts-v1-shared/di/di-token';
import { provideSingleton } from '@bcf-vanilla-ts-v1-shared/di/provide-singleton';
import { isDefined } from '@bcf-vanilla-ts-v1-shared/misc/pure-utils/is-defined';
import { isSSR } from '@bcf-vanilla-ts-v1-shared/misc/utils/is-ssr';
import type { UseStore } from 'idb-keyval';
import { clear, createStore, del, get, getMany, set, setMany, values } from 'idb-keyval';
import type { Observable } from 'rxjs';
import { Subject } from 'rxjs';
import { IndexdbStorageServer } from './indexdb-storage.server';

export class IndexdbStorage {
  private _didUpdate$: Subject<void> = new Subject<void>();
  // if functionality is migrated from IndexDb, this array allows removing old keys for e.g., 3 releases
  private readonly _oldKeys: string[] = [];

  constructor(
    private _workerToolkit: WorkerToolkit,
    private _bcfAppId: BcfAppId
  ) {
    this._removeOldKeys();
  }

  public async createStore(storeName: string): Promise<UseStore> {
    return createStore(`${this._bcfAppId}:${this._workerToolkit.lang}:${storeName}`, storeName);
  }

  public async set<T>(key: IDBValidKey, value: T, store?: UseStore): Promise<void> {
    await set(key, value, store);
    this._didUpdate$.next(undefined);
  }

  public async delete(key: IDBValidKey, store?: UseStore): Promise<void> {
    await del(key, store);
    this._didUpdate$.next(undefined);
  }

  public async clear(store?: UseStore): Promise<void> {
    await clear(store);
    this._didUpdate$.next(undefined);
  }

  public get<T>(key: IDBValidKey, store?: UseStore): Promise<T | undefined> {
    return get(key, store);
  }

  public async getMany<T>(keys: IDBValidKey[], store?: UseStore): Promise<T[]> {
    const results: (T | undefined)[] = await getMany(keys, store);
    return results.filter(isDefined);
  }

  public getAllValues<T>(store?: UseStore): Promise<T[]> {
    return values(store);
  }

  public async setMany<T>(entries: [IDBValidKey, T][], store?: UseStore): Promise<void> {
    await setMany(entries, store);
    this._didUpdate$.next(undefined);
  }

  public didUpdated(): Observable<void> {
    return this._didUpdate$.asObservable();
  }

  private _removeOldKeys(): void {
    for (const key of this._oldKeys) {
      this.delete(key);
    }
  }
}
export function provideIndexdbStorage(): IndexdbStorage {
  if (isSSR()) {
    return provideSingleton(
      IndexdbStorage,
      () =>
        new IndexdbStorageServer(
          provideDiTokenValue(BCF_APP_ID),
          provideWorkerToolkit().lang ?? 'ssr'
        ) as unknown as IndexdbStorage
    );
  }
  return provideSingleton(
    IndexdbStorage,
    () => new IndexdbStorage(provideWorkerToolkit(), provideDiTokenValue(BCF_APP_ID))
  );
}
